// Process.jsx - Professional & Corporate Design
import React, { useEffect, useRef, useState } from 'react';
import '../../../assets/css/student/donorscreen/Process.css';

const Process = () => {
  const sectionRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, observerOptions);

    // Observe all step cards
    const stepCards = sectionRef.current?.querySelectorAll('.process-step');
    stepCards?.forEach(card => observer.observe(card));

    // Auto-progress animation
    const progressInterval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % 4);
    }, 3000);

    // Cleanup
    return () => {
      stepCards?.forEach(card => observer.unobserve(card));
      clearInterval(progressInterval);
    };
  }, []);

  const steps = [
    {
      number: "01",
      title: "You Donate",
      description: "Your contribution is received securely",
      icon: (
        <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="14" y="18" width="28" height="24" rx="3" fill="currentColor" fillOpacity="0.15"/>
          <path d="M22 18V16C22 14.3431 23.3431 13 25 13H31C32.6569 13 34 14.3431 34 16V18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
          <rect x="16" y="20" width="24" height="20" rx="2" stroke="currentColor" strokeWidth="2.5"/>
          <path d="M24 28L26.5 30.5L32 25" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="44" cy="40" r="8" fill="currentColor" fillOpacity="0.1"/>
          <path d="M41 40L43 42L47 38" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      color: "#2E7D32"
    },
    {
      number: "02",
      title: "Funds Allocated",
      description: "We assign resources to students in need",
      icon: (
        <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="12" y="16" width="32" height="28" rx="3" fill="currentColor" fillOpacity="0.15"/>
          <rect x="16" y="20" width="24" height="20" rx="2" stroke="currentColor" strokeWidth="2.5"/>
          <line x1="20" y1="26" x2="36" y2="26" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <line x1="20" y1="30" x2="32" y2="30" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <line x1="20" y1="34" x2="34" y2="34" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="42" cy="38" r="6" fill="currentColor" fillOpacity="0.2"/>
          <path d="M40 38L41.5 39.5L44 37" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      color: "#1565C0"
    },
    {
      number: "03",
      title: "Support Provided",
      description: "Tuition, books, and essentials covered",
      icon: (
        <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M28 14L16 20V28C16 34 20 40 28 44C36 40 40 34 40 28V20L28 14Z" fill="currentColor" fillOpacity="0.15"/>
          <path d="M28 16L18 21V28C18 33 21 38 28 42C35 38 38 33 38 28V21L28 16Z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          <rect x="24" y="12" width="8" height="4" rx="2" fill="currentColor" fillOpacity="0.3"/>
          <circle cx="44" cy="40" r="8" fill="currentColor" fillOpacity="0.2"/>
          <text x="44" y="44" fontSize="10" fontWeight="bold" fill="currentColor" textAnchor="middle">$</text>
        </svg>
      ),
      color: "#F57C00"
    },
    {
      number: "04",
      title: "Student Success",
      description: "Empowered students excel academically",
      icon: (
        <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="28" cy="22" r="7" fill="currentColor" fillOpacity="0.15"/>
          <circle cx="28" cy="22" r="6" stroke="currentColor" strokeWidth="2.5"/>
          <path d="M16 42C16 35 21 30 28 30C35 30 40 35 40 42V44H16V42Z" fill="currentColor" fillOpacity="0.15"/>
          <path d="M18 42C18 36 22 32 28 32C34 32 38 36 38 42" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M36 16L38 14L40 16M36 20L38 18L40 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="38" cy="17" r="1.5" fill="currentColor"/>
        </svg>
      ),
      color: "#C62828"
    }
  ];

  return (
    <section className="donation-works-section" ref={sectionRef}>
      <div className="donation-works-container">
        
        {/* Section Header */}
        <div className="section-header">
          <div className="header-badge">OUR IMPACT PROCESS</div>
          <h1 className="section-main-title">How Your Donation Works</h1>
          <p className="section-subtitle">
            A transparent, efficient journey from your generosity to student success
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="progress-track">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${((activeStep + 1) / 4) * 100}%` }}
            />
          </div>
        </div>

        {/* Process Steps */}
        <div className="process-steps">
          {steps.map((step, index) => (
            <div 
              key={index}
              className={`process-step ${index <= activeStep ? 'active' : ''}`}
              style={{ '--step-color': step.color }}
            >
              <div className="step-card">
                <div className="step-number-badge">
                  <span>{step.number}</span>
                </div>
                
                <div className="step-icon-wrapper">
                  <div className="icon-background"></div>
                  <div className="step-icon" style={{ color: step.color }}>
                    {step.icon}
                  </div>
                </div>

                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>

                <div className="step-connector">
                  <div className="connector-line"></div>
                  <div className="connector-arrow">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M5 10H15M15 10L11 6M15 10L11 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="stats-section">
          <div className="stat-item">
            <div className="stat-number">10,000+</div>
            <div className="stat-label">Students Supported</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">$2.5M+</div>
            <div className="stat-label">Total Donations</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">95%</div>
            <div className="stat-label">Success Rate</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">50+</div>
            <div className="stat-label">Partner Schools</div>
          </div>
        </div>

        {/* Call to Action */}
        {/* <div className="cta-section">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Make an Impact?</h2>
            <p className="cta-text">
              Join thousands of donors creating opportunities for deserving students
            </p>
            <div className="cta-buttons">
              <button 
                className="cta-primary-btn"
                onClick={() => console.log('Become a Donor clicked')}
                aria-label="Become a donor"
              >
                <span>Become a Donor</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10H16M16 10L12 6M16 10L12 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className="cta-secondary-btn"
                onClick={() => console.log('Learn More clicked')}
                aria-label="Learn more about the process"
              >
                Learn More
              </button>
            </div>
          </div>
        </div> */}

      </div>
    </section>
  );
};

export default Process;